import { createStyles } from "@mantine/emotion";

import { color } from "../../constant/color";

/**
 * Shared look for every `PasswordInput` in the platform — the login field, the
 * password-creation and reset pages, and the admin/info logins.
 *
 * Lives here rather than being repeated per screen so the eye button and the
 * focus ring cannot drift apart between them: they are the same control to a
 * user, and they were previously styled (or not) one file at a time.
 *
 * Apply both together:
 *   <PasswordInput
 *     className={classes.wrapper}
 *     classNames={{ visibilityToggle: classes.visibilityToggle }}
 *   />
 */
export const usePasswordInputStyles = createStyles(() => ({
  /**
   * Focus ring, matching the login page's inputs.
   *
   * Targets `[data-variant]`, not `input` — Mantine renders the bordered box
   * and the native `<input>` as separate elements for PasswordInput (the box is
   * a `<div>` one level up, holding both the input and the visibility toggle),
   * so a plain `input` selector misses it there even though it works for a
   * plain TextInput, where the two collapse into one element. Both slots carry
   * `data-variant`, so this reaches the actual bordered element either way.
   *
   * Overriding `--input-bd-focus` is what recolours the ring: Mantine's own
   * focus rule reads that variable, and a plain `borderColor` here would lose
   * to it on specificity. The idle border is deliberately left at Mantine's
   * default grey — this is a focus affordance, not a permanent accent.
   */
  wrapper: {
    "& [data-variant]": {
      "--input-bd-focus": color.FHGREEN,
    },
  },

  /**
   * Show/hide eye button.
   *
   * Mantine renders it as an ActionIcon, which fills with a grey wash on hover.
   * The brand treatment is the opposite: the button stays transparent and the
   * icon itself turns green, so the affordance reads without a box appearing
   * under the cursor.
   *
   * `&:hover:hover` because Mantine's own hover rule is (0,2,0) via `:where()`,
   * so a single `&:hover` ties and loses on load order. Same doubling as the
   * NavLink cascade note in CLAUDE.md.
   */
  visibilityToggle: {
    color: "#868e96",
    "&:hover:hover": {
      background: "transparent",
      color: color.FHGREEN,
    },
  },
}));

export default usePasswordInputStyles;
